import { useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { compareReadableLabels } from '../utils/compareReadableLabels.js';
import { cx } from '../utils/cx.js';
import { Input } from './Input.js';
import { Popover } from './Popover.js';
import styles from './SearchableSelect.module.css';

export type SearchableSelectOption = {
  value: string;
  label: string;
  disabled?: boolean;
  /** Optional leading glyph (icons, readiness ring, …). */
  leading?: ReactNode;
};

export type SearchableSelectProps = {
  label?: string;
  hint?: string;
  error?: string;
  options: SearchableSelectOption[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  required?: boolean;
  searchPlaceholder?: string;
  listAriaLabel?: string;
  id?: string;
  className?: string;
  showErrorText?: boolean;
  /** When false, keep `options` order (blanks still float first). Default true. */
  sortLabels?: boolean;
  'aria-describedby'?: string;
};

export function SearchableSelect({
  label,
  hint,
  error,
  options,
  value,
  onChange,
  disabled,
  required,
  searchPlaceholder = 'Rechercher…',
  listAriaLabel = 'Options',
  id: idProp,
  className,
  showErrorText = true,
  sortLabels = true,
  'aria-describedby': ariaDescribedBy,
}: SearchableSelectProps) {
  const autoId = useId();
  const fieldId = idProp ?? autoId;
  const listboxId = `${fieldId}-listbox`;
  const errorId = `${fieldId}-error`;
  const describedBy = [ariaDescribedBy, error ? errorId : null].filter(Boolean).join(' ') || undefined;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const typeaheadRef = useRef('');
  const typeaheadTimerRef = useRef<number | null>(null);

  const filtered = useMemo(() => {
    const blanks = options.filter((o) => !o.value);
    const rest = options.filter((o) => o.value);
    if (sortLabels) rest.sort((a, b) => compareReadableLabels(a.label, b.label));
    const sorted = [...blanks, ...rest];
    const q = query.trim().toLowerCase();
    if (!q) return sorted;
    return sorted.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query, sortLabels]);

  const selected = options.find((o) => o.value === value);
  const selectedLabel = selected?.label ?? '—';

  const selectableIndices = useMemo(
    () => filtered.map((o, i) => (o.disabled ? -1 : i)).filter((i) => i >= 0),
    [filtered],
  );

  const pickIndex = (index: number) => {
    const opt = filtered[index];
    if (!opt || opt.disabled) return;
    onChange(opt.value);
    setOpen(false);
    setQuery('');
  };

  const moveActive = (delta: number) => {
    if (!selectableIndices.length) return;
    const currentPos = selectableIndices.indexOf(activeIndex);
    const nextPos =
      currentPos < 0
        ? delta > 0
          ? 0
          : selectableIndices.length - 1
        : (currentPos + delta + selectableIndices.length) % selectableIndices.length;
    setActiveIndex(selectableIndices[nextPos] ?? selectableIndices[0] ?? 0);
  };

  const handleTriggerKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        const selectedIdx = filtered.findIndex((o) => o.value === value && !o.disabled);
        setActiveIndex(selectedIdx >= 0 ? selectedIdx : (selectableIndices[0] ?? 0));
      } else if (e.key === 'ArrowDown') {
        moveActive(1);
      } else if (e.key === 'ArrowUp') {
        moveActive(-1);
      }
      return;
    }
    if (!open && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      e.preventDefault();
      setOpen(true);
      const nextQuery = `${typeaheadRef.current}${e.key}`.toLowerCase();
      typeaheadRef.current = nextQuery;
      if (typeaheadTimerRef.current) window.clearTimeout(typeaheadTimerRef.current);
      typeaheadTimerRef.current = window.setTimeout(() => {
        typeaheadRef.current = '';
      }, 500);
      const idx = filtered.findIndex((o) => !o.disabled && o.label.toLowerCase().startsWith(nextQuery));
      if (idx >= 0) setActiveIndex(idx);
    }
  };

  const handleListKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      moveActive(1);
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      moveActive(-1);
      return;
    }
    if (e.key === 'Home') {
      e.preventDefault();
      setActiveIndex(selectableIndices[0] ?? 0);
      return;
    }
    if (e.key === 'End') {
      e.preventDefault();
      setActiveIndex(selectableIndices[selectableIndices.length - 1] ?? 0);
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      pickIndex(activeIndex);
    }
  };

  const trigger = (
    <button
      id={fieldId}
      type="button"
      className={`${styles.trigger} ${error ? styles.triggerError : ''}`}
      disabled={disabled}
      role="combobox"
      aria-expanded={open}
      aria-controls={listboxId}
      aria-invalid={!!error}
      aria-required={required || undefined}
      aria-describedby={describedBy}
      aria-haspopup="listbox"
      onKeyDown={handleTriggerKeyDown}
    >
      <span className={styles.triggerLabel}>
        {selected?.leading ? <span className={styles.optionLeading}>{selected.leading}</span> : null}
        <span className={styles.optionText}>{selectedLabel}</span>
      </span>
    </button>
  );

  return (
    <div className={cx(styles.wrap, className)}>
      {label ? (
        <label className={styles.label} htmlFor={fieldId}>
          {label}
        </label>
      ) : null}
      <Popover
        opened={open}
        onChange={(next) => {
          setOpen(next);
          if (!next) setQuery('');
        }}
        portal
        align="start"
        portalMinWidth={280}
        portalMaxWidth={420}
        portalEstimatedHeight={320}
        portalZIndex="var(--wp-z-popover-over-modal)"
        toggleOnTargetClick={!disabled}
        target={trigger}
      >
        <div
          className={styles.panel}
          onKeyDown={(e) => {
            handleListKeyDown(e);
            if (e.key === 'Escape') setOpen(false);
          }}
        >
          <Input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={searchPlaceholder}
            aria-label={`${listAriaLabel} — recherche`}
          />
          <div className={styles.list} id={listboxId} role="listbox" aria-label={listAriaLabel}>
            {filtered.map((o, index) => (
              <button
                key={o.value || '__empty__'}
                type="button"
                role="option"
                aria-selected={value === o.value}
                aria-disabled={o.disabled || undefined}
                disabled={o.disabled}
                className={`${styles.option} ${o.disabled ? styles.optionDisabled : ''} ${value === o.value ? styles.optionActive : ''} ${open && index === activeIndex ? styles.optionFocused : ''}`}
                onMouseEnter={() => !o.disabled && setActiveIndex(index)}
                onClick={() => {
                  if (o.disabled) return;
                  onChange(o.value);
                  setOpen(false);
                }}
              >
                {o.leading ? <span className={styles.optionLeading}>{o.leading}</span> : null}
                <span className={styles.optionText}>{o.label}</span>
              </button>
            ))}
            {filtered.length === 0 ? <p className={styles.empty}>Aucun résultat</p> : null}
          </div>
        </div>
      </Popover>
      {error && showErrorText ? (
        <span id={errorId} className={styles.errText} role="alert">
          {error}
        </span>
      ) : hint ? (
        <span className={styles.hint}>{hint}</span>
      ) : null}
    </div>
  );
}
